import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

const SITE_URL = 'https://www.chatajilovanka.cz'
const SHARE_IMAGE = `${SITE_URL}/og-jilovanka.jpg`
const BRAND_LOGO = `${SITE_URL}/favicon.png`

const pages = {
  '/': {
    title: 'Chata Jílovanka | Ubytování v Orlických horách',
    description: 'Chata Jílovanka ve Zdobnici nabízí ubytování až pro 34 hostů v 9 pokojích s vlastní koupelnou. Ideální pro rodiny, skupiny, svatby a firemní pobyty.',
    breadcrumb: 'Úvod'
  },
  '/aktivity': {
    title: 'Aktivity v Orlických horách | Chata Jílovanka',
    description: 'Tipy na výlety a aktivity v okolí Zdobnice. Turistika, cyklistika, lyžování a zážitky v Orlických horách nedaleko Chaty Jílovanka.',
    breadcrumb: 'Aktivity'
  },
  '/galerie': {
    title: 'Fotogalerie ubytování | Chata Jílovanka',
    description: 'Prohlédněte si pokoje, společenské prostory i okolí Chaty Jílovanka ve Zdobnici. Ubytování pro skupiny v srdci Orlických hor.',
    breadcrumb: 'Fotogalerie'
  },
  '/cenik': {
    title: 'Ceník ubytování | Chata Jílovanka Zdobnice',
    description: 'Aktuální ceny pronájmu Chaty Jílovanka pro letní, zimní i vedlejší sezónu. Přehled cen pobytů, kauce a důležitých podmínek ubytování.',
    breadcrumb: 'Ceník'
  },
  '/obsazenost': {
    title: 'Obsazenost a volné termíny | Chata Jílovanka',
    description: 'Zjistěte dostupnost Chaty Jílovanka a vyberte si volný termín pro skupinový pobyt, rodinnou dovolenou nebo oslavu v Orlických horách.',
    breadcrumb: 'Obsazenost'
  },
  '/vybaveni': {
    title: 'Vybavení chaty a pokojů | Chata Jílovanka',
    description: 'Kompletní vybavení Chaty Jílovanka: 9 pokojů s vlastní koupelnou, Wi-Fi, parkování, společenské prostory, restaurace a venkovní terasa.',
    breadcrumb: 'Vybavení'
  },
  '/kontakt': {
    title: 'Kontakt a rezervace | Chata Jílovanka Zdobnice',
    description: 'Kontaktujte Chatu Jílovanka ve Zdobnici. Telefon +420 775 112 341, e-mail info@penzionjilovanka.cz, adresa Zdobnice 5, Orlické hory.',
    breadcrumb: 'Kontakt'
  }
}

const setMeta = (selector, attributes) => {
  let element = document.head.querySelector(selector)
  if (!element) {
    element = document.createElement('meta')
    document.head.appendChild(element)
  }
  Object.entries(attributes).forEach(([name, value]) => element.setAttribute(name, value))
}

const SEO = () => {
  const { pathname } = useLocation()

  useEffect(() => {
    const normalizedPath = pathname !== '/' ? pathname.replace(/\/$/, '') : '/'
    const page = pages[normalizedPath] || pages['/']
    const canonicalUrl = `${SITE_URL}${normalizedPath === '/' ? '' : normalizedPath}`

    document.documentElement.lang = 'cs'
    document.title = page.title

    setMeta('meta[name="description"]', { name: 'description', content: page.description })
    setMeta('meta[name="robots"]', {
      name: 'robots',
      content: 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
    })
    setMeta('meta[property="og:type"]', { property: 'og:type', content: 'website' })
    setMeta('meta[property="og:locale"]', { property: 'og:locale', content: 'cs_CZ' })
    setMeta('meta[property="og:site_name"]', { property: 'og:site_name', content: 'Chata Jílovanka' })
    setMeta('meta[property="og:title"]', { property: 'og:title', content: page.title })
    setMeta('meta[property="og:description"]', { property: 'og:description', content: page.description })
    setMeta('meta[property="og:url"]', { property: 'og:url', content: canonicalUrl })
    setMeta('meta[property="og:image"]', { property: 'og:image', content: SHARE_IMAGE })
    setMeta('meta[property="og:image:alt"]', { property: 'og:image:alt', content: 'Interiér Chaty Jílovanka ve Zdobnici' })
    setMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' })
    setMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: page.title })
    setMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: page.description })
    setMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: SHARE_IMAGE })

    let canonical = document.head.querySelector('link[rel="canonical"]')
    if (!canonical) {
      canonical = document.createElement('link')
      canonical.rel = 'canonical'
      document.head.appendChild(canonical)
    }
    canonical.href = canonicalUrl

    const structuredData = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebSite',
          '@id': `${SITE_URL}/#website`,
          url: `${SITE_URL}/`,
          name: 'Chata Jílovanka',
          alternateName: 'Jílovanka',
          inLanguage: 'cs-CZ'
        },
        {
          '@type': 'LodgingBusiness',
          '@id': `${SITE_URL}/#lodging`,
          name: 'Chata Jílovanka',
          legalName: 'Chata Jílovanka s.r.o.',
          url: `${SITE_URL}/`,
          logo: BRAND_LOGO,
          image: SHARE_IMAGE,
          description: pages['/'].description,
          telephone: '+420775112341',
          email: 'info@penzionjilovanka.cz',
          address: {
            '@type': 'PostalAddress',
            streetAddress: 'Zdobnice 5',
            addressLocality: 'Zdobnice',
            postalCode: '516 01',
            addressRegion: 'Královéhradecký kraj',
            addressCountry: 'CZ'
          },
          geo: {
            '@type': 'GeoCoordinates',
            latitude: 50.24095789519349,
            longitude: 16.4046010244367
          },
          numberOfRooms: 9,
          petsAllowed: false,
          smokingAllowed: false,
          amenityFeature: [
            { '@type': 'LocationFeatureSpecification', name: 'Wi-Fi zdarma', value: true },
            { '@type': 'LocationFeatureSpecification', name: 'Parkování zdarma', value: true },
            { '@type': 'LocationFeatureSpecification', name: 'Vlastní sociální zařízení na pokojích', value: true },
            { '@type': 'LocationFeatureSpecification', name: 'Venkovní terasa', value: true }
          ]
        },
        {
          '@type': 'BreadcrumbList',
          '@id': `${canonicalUrl}#breadcrumb`,
          itemListElement: normalizedPath === '/' ? [
            { '@type': 'ListItem', position: 1, name: 'Chata Jílovanka', item: `${SITE_URL}/` }
          ] : [
            { '@type': 'ListItem', position: 1, name: 'Chata Jílovanka', item: `${SITE_URL}/` },
            { '@type': 'ListItem', position: 2, name: page.breadcrumb, item: canonicalUrl }
          ]
        }
      ]
    }

    let jsonLd = document.getElementById('seo-structured-data')
    if (!jsonLd) {
      jsonLd = document.createElement('script')
      jsonLd.id = 'seo-structured-data'
      jsonLd.type = 'application/ld+json'
      document.head.appendChild(jsonLd)
    }
    jsonLd.textContent = JSON.stringify(structuredData)
  }, [pathname])

  return null
}

export default SEO
